import React, { useState, useEffect } from 'react';
import { ArrowRight, Package, Users, Building2, Shield, TrendingUp, Phone, Menu, X, Mail, ChevronDown, Zap, Clock, Leaf, FlaskConical, Instagram, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../config/emailjs';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger } from
'../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger } from
'../components/ui/accordion';

const LandingPage = () => {
  const whatsappNumber = '5511993089192';
  const [selectedFlavor, setSelectedFlavor] = useState('chocolate');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    empresa: '',
    individuos: '',
    volume: '',
    mensagem: ''
  });

  // Intersection observers for animations
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [featuresRef, featuresInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [impactRef, impactInView] = useInView({ triggerOnce: true, threshold: 0.3 });

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const flavors = [
  {
    id: 'chocolate',
    name: 'Chocolate',
    image: 'https://customer-assets.emergentagent.com/job_3d1836e7-56ba-49a2-9c7d-1b54a4fceb94/artifacts/ihs3eoc0_Embalagem%20Crescinutri%20-%20Chocolate%201kg.PNG',
    color: '#8B4513'
  },
  {
    id: 'morango',
    name: 'Morango',
    image: 'https://customer-assets.emergentagent.com/job_3d1836e7-56ba-49a2-9c7d-1b54a4fceb94/artifacts/c4wt0g6x_Embalagem%20Crescinutri%20-%20Morango%201kg.PNG',
    color: '#DC143C'
  },
  {
    id: 'baunilha',
    name: 'Baunilha',
    image: 'https://customer-assets.emergentagent.com/job_3d1836e7-56ba-49a2-9c7d-1b54a4fceb94/artifacts/idzsqa3v_Embalagem%20Crescinutri%20-%20Baunilha%201kg.PNG',
    color: '#F4E4C1'
  }];


  const nutrients = [
    { name: 'Vitamina D', value: '5μg', vd: '100% VD', benefit: 'Regula absorção de cálcio e fósforo; favorece mineralização óssea e contribui para força muscular e saúde imunológica' },
    { name: 'Cálcio', value: '740mg', vd: '74% VD', benefit: 'Essencial para alcançar massa óssea ótima, contribui para saúde dentária e função muscular' },
    { name: 'Ferro', value: '10mg', vd: '71% VD', benefit: 'Crucial para desenvolvimento cognitivo, atenção, comportamento e desempenho escolar; previne anemia ferropriva' },
    { name: 'Iodo', value: '96μg', vd: '74% VD', benefit: 'Essencial para desenvolvimento neurológico; prevenção de retardo intelectual e problemas de crescimento' },
    { name: 'Vitamina C', value: '30mg', vd: '66% VD', benefit: 'Necessária para integridade de pele, gengivas e vasos; apoia cicatrização e aumenta absorção de ferro alimentar' },
    { name: 'Vitamina E', value: '6,2mg', vd: '60% VD', benefit: 'Antioxidante que protege células do estresse oxidativo e contribui para o sistema imune' },
    { name: 'Ácido Fólico', value: '120μg', vd: '50% VD', benefit: 'Apoio ao crescimento, maturação celular e prevenção de anemias megaloblásticas' },
    { name: 'Vitamina B3', value: '7,2mg', vd: '44% VD', benefit: 'Ajuda conversão de alimentos em energia, manutenção da pele e do trato digestivo' },
    { name: 'Vitamina A', value: '340μg', vd: '42% VD', benefit: 'Mantém visão saudável, fortalece defesa contra infecções e apoia crescimento' },
    { name: 'Vitamina B1', value: '0,46mg', vd: '38% VD', benefit: 'Sustenta produção de energia, crescimento e função cerebral' },
    { name: 'Vitamina B6', value: '0,5mg', vd: '38% VD', benefit: 'Apoia desenvolvimento cerebral, formação de neurotransmissores e produção de hemoglobina' },
    { name: 'Zinco', value: '2,6mg', vd: '37% VD', benefit: 'Essencial para crescimento e desenvolvimento, resposta imune e cicatrização' },
    { name: 'Molibdênio', value: '16,4μg', vd: '36% VD', benefit: 'Requisito para metabolismo normal de proteínas e detoxificação enzimática' },
    { name: 'Vitamina B2', value: '0,46mg', vd: '34% VD', benefit: 'Apoio ao crescimento celular, saúde da pele e olhos' },
    { name: 'Cromo', value: '12μg', vd: '34% VD', benefit: 'Apoia o metabolismo energético e regulação de carboidratos' },
    { name: 'Vitamina B5', value: '1,5mg', vd: '30% VD', benefit: 'Apoio ao metabolismo energético geral, crescimento e manutenção dos tecidos' },
    { name: 'Magnésio', value: '70mg', vd: '27% VD', benefit: 'Contribui para função nervosa/muscular adequada, saúde óssea e equilíbrio eletrolítico' },
    { name: 'Manganês', value: '0,6mg', vd: '26% VD', benefit: 'Apoio ao metabolismo, crescimento ósseo e ao sistema antioxidante' },
    { name: 'Vitamina B12', value: '0,6μg', vd: '25% VD', benefit: 'Essencial para desenvolvimento neurológico e cognitivo; previne anemia megaloblástica' },
    { name: 'Selênio', value: '8,25μg', vd: '24% VD', benefit: 'Protege contra dano oxidativo, apoia função imunológica e regulação hormonal tireoidiana' },
    { name: 'Cobre', value: '200μg', vd: '22% VD', benefit: 'Apoia crescimento, desenvolvimento do sistema nervoso e metabolismo do ferro' },
    { name: 'Fósforo', value: '105mg', vd: '15% VD', benefit: 'Necessário para crescimento ósseo, função celular e metabolismo energético' },
    { name: 'Colina', value: '82,5mg', vd: '15% VD', benefit: 'Fundamental para desenvolvimento cerebral, memória e função neuromuscular' },
    { name: 'Potássio', value: '503mg', vd: '14% VD', benefit: 'Mantém função muscular normal, pressão arterial saudável e equilíbrio hídrico' },
    { name: 'Biotina', value: '30μg', vd: '100% VD', benefit: 'Contribui ao metabolismo energético e à saúde de pele, cabelo e unhas' },
    { name: 'Sódio', value: '107mg', vd: '5,5% VD', benefit: 'Mantém equilíbrio hídrico e eletrolítico, transmissão nervosa e contração muscular' }
  ];


  const impacts = [
  { value: '15-20%', label: 'Melhoria no desempenho escolar' },
  { value: '30%', label: 'Redução no absenteísmo escolar' },
  { value: '5-11', label: 'Pontos a mais de QI' }];


  const faqs = [
  {
    question: 'O CresciNutri é adequado para qual faixa etária?',
    answer: 'Para crianças a partir de 3 anos e adolescentes. Para crianças menores, recomendamos consultar um pediatra ou nutricionista antes do uso.'
  },
  {
    question: 'O produto tem registro ANVISA?',
    answer: 'A fábrica possui Registro ANVISA, Certificação MAPA e Licença Sanitária — todos os requisitos regulatórios para produção e comercialização de alimentos no Brasil, incluindo fornecimento a órgãos públicos.'
  },
  {
    question: 'Como preparar o CresciNutri?',
    answer: 'Misture 2 colheres de sopa (30g) em 200ml de água ou leite. Pronto em menos de 30 segundos. Pode ser adicionado também a frutas, vitaminas e cereais.'
  },
  {
    question: 'O produto contém glúten ou lactose?',
    answer: 'O CresciNutri é livre de glúten, sendo adequado para pessoas com doença celíaca ou sensibilidade ao glúten. O produto contém lactose. Pessoas com intolerância à lactose devem consultar um médico ou nutricionista antes do consumo.'
  },
  {
    question: 'Qual a validade do produto?',
    answer: '12 meses a partir da data de fabricação. Conservar em local seco e arejado.'
  },
  {
    question: 'As crianças e adolescentes aceitam o sabor?',
    answer: 'O CresciNutri tem três sabores desenvolvidos para agradar diferentes paladares: Chocolate, Morango e Baunilha, todos sem açúcar adicionado. Por poder ser misturado no leite ou em vitaminas de frutas, a incorporação na rotina é simples.'
  },
  {
    question: 'Posso misturar com outras bebidas além de água e leite?',
    answer: 'Sim. O CresciNutri pode ser adicionado a vitaminas de frutas, mingaus, cereais e outros preparos, o que ajuda especialmente quem tem paladar mais seletivo.'
  },
  {
    question: 'Como funciona a compra institucional?',
    answer: 'Entre em contato pelo WhatsApp. Nossa equipe envia proposta personalizada com especificações técnicas e documentação completa. Amostras técnicas estão disponíveis para avaliação prévia, sem nenhum compromisso de compra.'
  },
  {
    question: 'Nunca trabalhamos com esse fornecedor. Como ter segurança?',
    answer: 'Disponibilizamos toda a documentação da fábrica para análise prévia e enviamos amostras técnicas para que sua equipe avalie antes de qualquer decisão. Estamos disponíveis também para visita técnica à fábrica mediante agendamento.'
  },
  {
    question: 'O CresciNutri é mais caro do que o que já utilizamos?',
    answer: 'Provavelmente não quando a comparação é feita por nutriente entregue por real investido. O CresciNutri lidera em 14 nutrientes frente aos principais concorrentes do mercado e oferece economia de até 40% frente a programas tradicionais.'
  }];


  const handleWhatsApp = (message) => {
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Initialize EmailJS
      emailjs.init(EMAILJS_CONFIG.publicKey);
      
      // Prepare email parameters
      const templateParams = {
        from_name: formData.nome,
        from_email: formData.email,
        empresa: formData.empresa,
        individuos: formData.individuos,
        volume: formData.volume,
        mensagem: formData.mensagem || 'Nenhuma mensagem adicional.',
        to_email: 'crescinutri@outlook.com',
        reply_to: formData.email
      };

      // Send email using EmailJS
      const response = await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        templateParams
      );

      if (response.status === 200) {
        // Also save to MongoDB for backup
        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/send-institutional-proposal`,
            formData
          );
        } catch (dbError) {
          console.log('Backup to database failed, but email was sent successfully');
        }

        alert('✅ Solicitação enviada com sucesso! Nossa equipe entrará em contato em breve.');
        setIsDialogOpen(false);
        setFormData({ nome: '', email: '', empresa: '', individuos: '', volume: '', mensagem: '' });
      }
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Erro ao enviar solicitação. Por favor, tente novamente ou entre em contato via WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Free Shipping Banner */}
      <div className="shipping-banner">
        <div className="container">
          <div className="shipping-banner-content">
            <Package size={18} />
            <span>Frete grátis em compras acima de R$ 300</span>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="navigation-header">
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="navigation-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_3d1836e7-56ba-49a2-9c7d-1b54a4fceb94/artifacts/mm660xwi_Logo%20-%20CresciNutri%20-%20Sem%20Fundo.png"
                alt="CresciNutri"
                style={{ height: '160px', width: 'auto', mixBlendMode: 'multiply' }} />

            </div>
            <nav className="navigation-menu desktop-only">
              <a href="#produto" className="navigation-link">Produto</a>
              <a href="#beneficios" className="navigation-link">Benefícios</a>
              <a href="#nutricao" className="navigation-link">Nutrição</a>
              <a href="#comprar" className="navigation-link">Comprar</a>
            </nav>
            <button
              className="btn-primary desktop-only"
              onClick={() => handleWhatsApp('Olá! Gostaria de mais informações sobre o CresciNutri.')}>

              <Phone size={16} style={{ marginRight: '8px' }} />
              Fale com Especialista
            </button>
            <button
              className="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu">

              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen &&
        <div className="mobile-menu">
            <nav className="mobile-menu-nav">
              <a
              href="#produto"
              className="mobile-menu-link"
              onClick={() => setMobileMenuOpen(false)}>

                Produto
              </a>
              <a
              href="#beneficios"
              className="mobile-menu-link"
              onClick={() => setMobileMenuOpen(false)}>

                Benefícios
              </a>
              <a
              href="#nutricao"
              className="mobile-menu-link"
              onClick={() => setMobileMenuOpen(false)}>

                Nutrição
              </a>
              <a
              href="#comprar"
              className="mobile-menu-link"
              onClick={() => setMobileMenuOpen(false)}>

                Comprar
              </a>
              <button
              className="btn-primary"
              style={{ width: '100%', marginTop: '16px' }}
              onClick={() => {
                handleWhatsApp('Olá! Gostaria de mais informações sobre o CresciNutri.');
                setMobileMenuOpen(false);
              }}>

                <Phone size={16} style={{ marginRight: '8px' }} />
                Fale com Especialista
              </button>
            </nav>
          </div>
        }
      </header>

      {/* Hero Section - Tech Enhanced */}
      <section className="hero-section-tech section-padding">
        <div className="hero-tech-background">
          <div className="floating-particle" style={{ top: '10%', left: '15%', animationDelay: '0s' }}></div>
          <div className="floating-particle" style={{ top: '60%', left: '80%', animationDelay: '2s' }}></div>
          <div className="floating-particle" style={{ top: '30%', left: '70%', animationDelay: '4s' }}></div>
          <div className="floating-particle" style={{ top: '70%', left: '20%', animationDelay: '3s' }}></div>
        </div>
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="grid-two-column">
            <div className="hero-content-animated">
              <div className="tech-badge tech-badge-fit">
                <Zap size={14} />
                <span>Pó para Bebida Rica em Vitaminas e Minerais</span>
              </div>
              <p
                style={{
                  marginTop: '16px',
                  marginBottom: '12px',
                  fontSize: '16px',
                  fontWeight: 500,
                  color: '#1b4332',
                  letterSpacing: '-0.01em'
                }}>
                CresciNutri: Nutrição que Transforma Vidas
              </p>
              <h1 className="hero-headline-xl" style={{ marginBottom: '16px', marginTop: '8px' }}>
                <span className="text-gradient">26 vitaminas e minerais.</span> Um copo por dia.
              </h1>
              <h2 className="hero-subheadline" style={{ marginBottom: '20px' }}>
                Sem açúcar, sem glúten e com o maior retorno nutricional por real investido.
              </h2>
              <p className="body-large" style={{ marginBottom: '32px', color: '#4a5568', lineHeight: '1.7', fontSize: '16px' }}>
                CresciNutri é um pó para preparo de bebida enriquecido com vitaminas e minerais, desenvolvido para crianças e adolescentes. Único do mercado sem maltodextrina ou sacarose, com minerais quelatos para absorção real.
              </p>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <button
                  className="btn-hero-primary"
                  onClick={() => handleWhatsApp('Olá! Tenho interesse em adquirir o CresciNutri para minha família.')}>

                  Comprar Agora
                  <ArrowRight size={16} style={{ marginLeft: '8px' }} />
                </button>
                <button
                  className="btn-hero-secondary"
                  onClick={() => document.getElementById('nutricao').scrollIntoView({ behavior: 'smooth' })}>

                  Ver Informações Nutricionais
                </button>
              </div>
            </div>
            <div className="hero-image-container">
              <div className="image-glow"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_3d1836e7-56ba-49a2-9c7d-1b54a4fceb94/artifacts/q827l7as_familia%20produtos%20crescinutri.png"
                alt="Família CresciNutri"
                className="hero-main-image"
                style={{ transform: `translateY(${scrollY * 0.1}px)` }} />

            </div>
          </div>
        </div>
      </section>

      {/* Sobre o Produto */}
      <section id="produto" className="section-padding tech-section" ref={featuresRef}>
        <div className="container">
          <motion.div 
            style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto', marginBottom: '80px' }}
            initial={{ opacity: 0, y: 30 }}
            animate={featuresInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 className="hero-medium" style={{ marginBottom: '24px' }}>Ciência e <span className="text-gradient">Inovação</span> em cada porção</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Cada nutriente tem uma função. Cada porção tem um propósito.
            </p>
          </motion.div>

          <div className="grid-product-showcase">
            {[
              { icon: TrendingUp, title: 'Crescimento Saudável', desc: 'Cálcio e vitamina D trabalhando juntos para fortalecer ossos e dentes no período mais importante do crescimento.' },
              { icon: Shield, title: 'Sistema Imunológico', desc: 'Vitaminas A, C e E, zinco e selênio formando uma linha de defesa completa para o organismo em desenvolvimento.' },
              { icon: Zap, title: 'Prevenção de Anemia', desc: '71% da necessidade diária de ferro em uma porção, com vitamina C potencializando a absorção e garantindo energia e concentração.' }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card-tech"
                initial={{ opacity: 0, y: 50 }}
                animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <div className="feature-icon-tech">
                  <feature.icon size={32} />
                </div>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>{feature.title}</h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sabores */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>Três sabores <span className="text-gradient">deliciosos</span></h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Escolha o favorito da sua família</p>
          </div>

          <div className="flavors-grid">
            {flavors.map((flavor) =>
            <div
              key={flavor.id}
              className={`flavor-card-tech ${selectedFlavor === flavor.id ? 'active' : ''}`}
              onClick={() => setSelectedFlavor(flavor.id)}>

                <div className="flavor-image-container">
                  <img src={flavor.image} alt={flavor.name} className="flavor-image" />
                </div>
                <h3 className="heading-2" style={{ marginTop: '24px', textAlign: 'center', marginBottom: '16px' }}>{flavor.name}</h3>
                <button
                className="btn-primary-tech"
                style={{ width: '100%' }}
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById('comprar').scrollIntoView({ behavior: 'smooth' });
                }}>

                  Comprar
                  <ArrowRight size={16} style={{ marginLeft: '8px' }} />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Impacto - Movido para cá */}
      <section id="beneficios" className="section-padding" ref={impactRef}>
        <div className="container">
          <motion.div 
            style={{ textAlign: 'center', marginBottom: '80px' }}
            initial={{ opacity: 0, y: 30 }}
            animate={impactInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>Comprovado pela <span className="text-gradient">ciência</span></h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>O impacto da nutrição adequada no desenvolvimento de crianças e adolescentes</p>
          </motion.div>

          <div className="impact-grid">
            {impacts.map((impact, index) => (
              <motion.div 
                key={index} 
                className="impact-card-tech"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={impactInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <div className="impact-value">
                  {impactInView && (
                    impact.value.includes('-') ? (
                      <>
                        <CountUp end={parseInt(impact.value.split('-')[0])} duration={2.5} />
                        -
                        <CountUp end={parseInt(impact.value.split('-')[1])} duration={2.5} />
                        %
                      </>
                    ) : impact.value.includes('%') ? (
                      <>
                        <CountUp end={parseInt(impact.value)} duration={2.5} />%
                      </>
                    ) : (
                      <>
                        <CountUp end={parseInt(impact.value.split('-')[0])} duration={2.5} />
                        -
                        <CountUp end={parseInt(impact.value.split('-')[1])} duration={2.5} />
                      </>
                    )
                  )}
                </div>
                <p className="body-regular" style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>{impact.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Preços e Compra - MOVIDO PARA CÁ */}
      <section id="comprar" className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>Escolha a melhor <span className="text-gradient">opção</span> para você</h2>
          </div>

          <div className="pricing-grid">
            {/* B2C - Famílias */}
            <div className="pricing-card-tech">
              <div className="pricing-header">
                <div className="pricing-icon-tech">
                  <Users size={32} />
                </div>
                <h3 className="heading-2" style={{ marginTop: '16px' }}>Para Famílias</h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>Nutrição de qualidade para seus filhos</p>
              </div>

              <div className="pricing-options">
                <div className="price-option-tech">
                  <div>
                    <h4 className="heading-3">Embalagem 1kg</h4>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Ideal para experimentar</p>
                  </div>
                  <div className="price">R$ 120</div>
                </div>

                <div className="price-option-tech">
                  <div>
                    <h4 className="heading-3">Embalagem 10kg</h4>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Melhor custo-benefício</p>
                  </div>
                  <div className="price">R$ 900</div>
                </div>
              </div>

              <button
                className="btn-primary-tech"
                style={{ width: '100%', marginTop: '32px' }}
                onClick={() => handleWhatsApp('Olá! Sou responsável por uma família e tenho interesse em adquirir o CresciNutri. Gostaria de mais informações sobre preços e sabores disponíveis.')}>

                Falar com Especialista
                <Phone size={16} style={{ marginLeft: '8px' }} />
              </button>
            </div>

            {/* B2G - Instituições */}
            <div className="pricing-card-tech featured">
              <div className="featured-badge-tech">Compra em volume</div>
              <div className="pricing-header">
                <div className="pricing-icon-tech">
                  <Building2 size={32} />
                </div>
                <h3 className="heading-2" style={{ marginTop: '16px' }}>Para Instituições</h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>Governo, escolas, hospitais e projetos sociais</p>
              </div>

              <div className="institutional-benefits">
                <ul className="benefits-list">
                  <li className="body-regular">Condições especiais para compras em grande volume</li>
                  <li className="body-regular">Suporte técnico nutricional dedicado</li>
                  <li className="body-regular">Participação em licitações</li>
                  <li className="body-regular">Logística especializada para entregas de grandes volumes</li>
                  <li className="body-regular">Aprovado para programas governamentais</li>
                </ul>
              </div>

              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <button className="btn-primary-tech" style={{ width: '100%', marginTop: '32px' }}>
                    Solicitar Proposta Institucional
                    <Mail size={16} style={{ marginLeft: '8px' }} />
                  </button>
                </DialogTrigger>
                <DialogContent className="institutional-dialog-fixed">
                  <DialogHeader>
                    <DialogTitle className="heading-2">Solicitar Proposta Institucional</DialogTitle>
                    <DialogDescription className="body-regular" style={{ marginTop: '8px' }}>
                      Preencha os dados abaixo e nossa equipe entrará em contato.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleFormSubmit} className="institutional-form-fixed">
                    <div className="form-group">
                      <Label htmlFor="nome" className="body-regular">Nome *</Label>
                      <Input
                        id="nome"
                        required
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        placeholder="Seu nome completo" />

                    </div>
                    <div className="form-group">
                      <Label htmlFor="email" className="body-regular">E-mail *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="seu.email@exemplo.com" />

                    </div>
                    <div className="form-group">
                      <Label htmlFor="empresa" className="body-regular">Nome da Empresa/Órgão *</Label>
                      <Input
                        id="empresa"
                        required
                        value={formData.empresa}
                        onChange={(e) => setFormData({ ...formData, empresa: e.target.value })}
                        placeholder="Nome da instituição" />

                    </div>
                    <div className="form-group">
                      <Label htmlFor="individuos" className="body-regular">Indivíduos a serem atendidos *</Label>
                      <Input
                        id="individuos"
                        required
                        value={formData.individuos}
                        onChange={(e) => setFormData({ ...formData, individuos: e.target.value })}
                        placeholder="Ex: 500 crianças" />

                    </div>
                    <div className="form-group">
                      <Label htmlFor="volume" className="body-regular">Volume em kg *</Label>
                      <Input
                        id="volume"
                        required
                        value={formData.volume}
                        onChange={(e) => setFormData({ ...formData, volume: e.target.value })}
                        placeholder="Ex: 1000 kg" />

                    </div>
                    <div className="form-group">
                      <Label htmlFor="mensagem" className="body-regular">Mensagem adicional</Label>
                      <Textarea
                        id="mensagem"
                        value={formData.mensagem}
                        onChange={(e) => setFormData({ ...formData, mensagem: e.target.value })}
                        placeholder="Conte-nos mais sobre sua necessidade..."
                        rows={3} />

                    </div>
                    <Button type="submit" className="btn-submit-fixed" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <div className="spinner-border" style={{ width: '16px', height: '16px', marginRight: '8px' }}></div>
                          Enviando...
                        </>
                      ) : (
                        <>
                          Enviar Solicitação
                          <Mail size={16} style={{ marginLeft: '8px' }} />
                        </>
                      )}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </section>

      {/* Fábrica Certificada e Habilitada */}
      <section className="section-padding certifications-section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <h2 className="hero-medium certifications-title">
              Fábrica <span className="text-gradient">Certificada e Habilitada</span>
            </h2>
          </div>

          <div className="certifications-grid">
            {[
              'Certificação MAPA',
              'Registro ANVISA',
              'Licença Sanitária',
              'Certidões Negativas de Débito (CNDs)',
              'CRF — Certificado de Regularidade do FGTS',
              'Alvará de Bombeiros',
              'Comprovação de Capacidade Técnica',
              'Manual de Boas Práticas e Qualidade (MBPQ)'
            ].map((item, idx) => (
              <div key={idx} className="certification-item">
                <CheckCircle2 size={22} className="certification-check" strokeWidth={2.25} />
                <span className="certification-label">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Embalagem Personalizada */}
      <section className="section-padding custom-packaging-section">
        <div className="container">
          <div className="custom-packaging-wrap">
            <h2 className="hero-medium custom-packaging-title">
              Embalagem <span className="text-gradient">Personalizada</span> para o Seu Programa
            </h2>
            <div className="custom-packaging-body">
              <p>
                O CresciNutri pode ser fornecido com a <span className="text-gradient">identidade visual do seu município, estado ou programa de governo</span>, reforçando a comunicação das ações de saúde e nutrição para a população atendida.
              </p>
              <p>
                Ideal para programas de merenda escolar e segurança alimentar que desejam fortalecer sua presença junto às comunidades beneficiadas.
              </p>
            </div>
            <ul className="custom-packaging-features">
              <li>
                <CheckCircle2 size={22} className="certification-check" strokeWidth={2.25} />
                <span>Logo do município, estado ou programa aplicada na embalagem</span>
              </li>
              <li>
                <CheckCircle2 size={22} className="certification-check" strokeWidth={2.25} />
                <span>Disponível em variadas opções de gramaturas e embalagens</span>
              </li>
            </ul>
          </div>
        </div>
      </section>



      {/* Informações do Produto - MELHORADO */}
      <section className="section-padding tech-section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>Tecnologia em <span className="text-gradient">cada detalhe</span></h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Desenvolvido para facilitar o seu dia a dia</p>
          </div>

          <div className="product-info-grid-tech">
            <div className="product-info-card-tech">
              <div className="info-icon-tech">
                <FlaskConical size={28} />
              </div>
              <h4 className="heading-3" style={{ marginBottom: '8px' }}>Formato</h4>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>Pó para bebida</p>
              <div className="info-detail">Tecnologia avançada de dissolução rápida</div>
            </div>
            
            <div className="product-info-card-tech">
              <div className="info-icon-tech">
                <Package size={28} />
              </div>
              <h4 className="heading-3" style={{ marginBottom: '8px' }}>Porção</h4>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>30g (2 colheres de sopa)</p>
              <div className="info-detail">Dose ideal para absorção ótima de nutrientes</div>
            </div>
            
            <div className="product-info-card-tech">
              <div className="info-icon-tech">
                <Zap size={28} />
              </div>
              <h4 className="heading-3" style={{ marginBottom: '8px' }}>Preparo</h4>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>Misturar com 200ml de água ou leite</p>
              <div className="info-detail">Pronto em menos de 30 segundos</div>
            </div>
            
            <div className="product-info-card-tech">
              <div className="info-icon-tech">
                <Leaf size={28} />
              </div>
              <h4 className="heading-3" style={{ marginBottom: '8px' }}>Conservação</h4>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>Local seco e arejado</p>
              <div className="info-detail">Embalagem inteligente com tecnologia antioxidante</div>
            </div>
            
            <div className="product-info-card-tech">
              <div className="info-icon-tech">
                <Clock size={28} />
              </div>
              <h4 className="heading-3" style={{ marginBottom: '8px' }}>Validade</h4>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>12 meses</p>
              <div className="info-detail">Sistema de preservação de nutrientes avançado</div>
            </div>
          </div>
        </div>
      </section>

      {/* Por que escolher CresciNutri */}
      <section className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>Por que escolher <span className="text-gradient">CresciNutri</span>?</h2>
          </div>

          <div className="why-choose-grid">
            <div className="why-choose-card-tech">
              <h3 className="heading-2" style={{ marginBottom: '24px' }}>Qualidade Superior</h3>
              <ul className="benefits-list">
                <li className="body-regular">Fórmula desenvolvida por especialistas em nutrição</li>
                <li className="body-regular">Ingredientes de alta qualidade</li>
                <li className="body-regular">Sem adição de açúcar, livre de glúten e adequado para dietas restritivas</li>
              </ul>
            </div>

            <div className="why-choose-card-tech">
              <h3 className="heading-2" style={{ marginBottom: '24px' }}>Praticidade</h3>
              <ul className="benefits-list">
                <li className="body-regular">Preparo rápido e simples: apenas misturar com líquido</li>
                <li className="body-regular">Sabores naturais aprovados por crianças e adolescentes</li>
                <li className="body-regular">Pode ser misturado com água, leite ou outras bebidas</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Informações Nutricionais */}
      <section id="nutricao" className="section-padding tech-section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>Informações <span className="text-gradient">Nutricionais</span></h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>26 nutrientes essenciais em cada porção de 30g</p>
          </div>

          <div className="nutrients-grid-compact">
            {nutrients.map((nutrient, index) => (
              <div key={index} className="nutrient-card-compact">
                <div className="nutrient-header-compact">
                  <h4 className="heading-3">{nutrient.name}</h4>
                  <div className="nutrient-values-compact">
                    <span className="nutrient-amount">{nutrient.value}</span>
                    <span className="nutrient-vd">{nutrient.vd}</span>
                  </div>
                </div>
                <p className="body-small nutrient-benefit">{nutrient.benefit}</p>
              </div>
            ))}
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <p className="body-small" style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              *VD = Valores Diários de referência baseados em uma dieta de 2.000 kcal
            </p>
          </div>
        </div>
      </section>


      {/* Urgência / Contexto - Janela de Desenvolvimento */}
      <section className="section-padding urgency-section">
        <div className="container">
          <div className="urgency-wrap">
            <h2 className="hero-medium urgency-title">
              A janela do <span className="text-gradient">desenvolvimento</span> não espera
            </h2>
            <div className="urgency-body">
              <p>
                A infância é o período mais crítico para o desenvolvimento cerebral humano. Entre os 3 e os 15 anos, o cérebro da criança forma conexões que definem sua capacidade de aprender, concentrar e raciocinar pelo resto da vida.
              </p>
              <p>
                Déficits nutricionais nessa fase não se recuperam completamente depois. Cada mês sem o aporte adequado de ferro, cálcio, iodo e vitamina D é um mês de desenvolvimento abaixo do potencial.
              </p>
              <p>
                Para gestores públicos, o calendário escolar não espera. Quanto antes o programa nutricional for implementado, mais crianças são beneficiadas naquele ano letivo e ao longo de toda a vida. <strong>Amostras técnicas disponíveis para avaliação antes de qualquer proposta.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

 
      {/* FAQ */}
      <section className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>Perguntas <span className="text-gradient">Frequentes</span></h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Tire suas dúvidas sobre o CresciNutri</p>
          </div>

          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <Accordion type="single" collapsible className="faq-accordion">
              {faqs.map((faq, index) =>
              <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="heading-3">{faq.question}</AccordionTrigger>
                  <AccordionContent className="body-regular">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <img
                src="https://customer-assets.emergentagent.com/job_3d1836e7-56ba-49a2-9c7d-1b54a4fceb94/artifacts/mm660xwi_Logo%20-%20CresciNutri%20-%20Sem%20Fundo.png"
                alt="CresciNutri"
                style={{ height: '160px', width: 'auto', marginBottom: '16px', mixBlendMode: 'multiply' }} />

              <p className="body-small" style={{ color: 'var(--text-secondary)', maxWidth: '300px' }}>
                Nutrição que transforma vidas
              </p>
            </div>

            <div className="footer-info">
              <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                SUPLEMENT INDUSTRIA NUTRACEUTICA LTDA<br />
                CNPJ: 44.220.875/0001-74<br />
                Avenida Pioneiros da Cidade 261<br />
                Piratininga - SP
              </p>
            </div>

            <div className="footer-contact">
              <h4 className="heading-3" style={{ marginBottom: '16px' }}>Fale Conosco</h4>
              <button
                className="btn-secondary"
                onClick={() => handleWhatsApp('Olá! Gostaria de mais informações sobre o CresciNutri.')}
                style={{ marginBottom: '12px', display: 'inline-flex', alignItems: 'center' }}>

                <Phone size={16} style={{ marginRight: '8px' }} />
                WhatsApp: (11) 99308-9192
              </button>
              <a
                href="mailto:crescinutri@outlook.com"
                className="btn-secondary"
                style={{ display: 'inline-flex', alignItems: 'center', marginBottom: '16px' }}>

                <Mail size={16} style={{ marginRight: '8px' }} />
                crescinutri@outlook.com
              </a>
              <a
                href="https://www.instagram.com/crescinutri?igsh=cTZqamZiOWp6Nmo4"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary instagram-link"
                style={{ display: 'inline-flex', alignItems: 'center' }}>

                <Instagram size={16} style={{ marginRight: '8px' }} />
                @crescinutri
              </a>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="body-small" style={{ color: 'var(--text-light)', textAlign: 'center' }}>
              © 2025 CresciNutri. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>);

};

export default LandingPage;